import React from 'react';

interface ToggleSwitchProps {
  label: string;
  name: string;
  checked: boolean;
  onChange: () => void;
  activeColor?: string;
  inactiveColor?: string;
  disabled?: boolean;
}

const colorMap: Record<string, string> = {
  primary:
    'border-primary/20 bg-primary/10 text-primary dark:border-primary/30 dark:bg-primary/15',
  secondary:
    'border-secondary/20 bg-secondary/10 text-secondary dark:border-secondary/30 dark:bg-secondary/15',
  dark: 'border-black/20 bg-black text-white dark:border-strokedark dark:bg-meta-4',
  gray: 'border-stroke bg-gray-2 text-bodydark2 dark:border-strokedark dark:bg-meta-4',
  light:
    'border-stroke bg-gray-2 text-black dark:border-strokedark dark:bg-boxdark-2 dark:text-white',
  warning:
    'border-warning/20 bg-warning/10 text-warning dark:border-warning/30 dark:bg-warning/15',
  danger:
    'border-danger/20 bg-danger/10 text-danger dark:border-danger/30 dark:bg-danger/15',
  success:
    'border-success/20 bg-success/10 text-success dark:border-success/30 dark:bg-success/15',
  info: 'border-meta-5/20 bg-meta-5/10 text-meta-5 dark:border-meta-5/30 dark:bg-meta-5/15',
};

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label,
  name,
  checked,
  onChange,
  activeColor,
  inactiveColor,
  disabled = false,
}) => {
  const isColoredMode = Boolean(activeColor && inactiveColor);

  const activeClasses = colorMap[activeColor ?? ''] ?? '';
  const inactiveClasses = colorMap[inactiveColor ?? ''] ?? '';

  const labelCursorClass = disabled
    ? 'cursor-not-allowed'
    : 'cursor-pointer';

  return (
    <div
      className={`
        flex items-start justify-between gap-4
        transition-opacity duration-200
        ${disabled ? 'opacity-60' : ''}
      `}
    >
      {isColoredMode ? (
        <span
          className={`
            inline-flex rounded-lg border
            px-2.5 py-1 text-sm font-medium
            transition-colors duration-200
            ${checked ? activeClasses : inactiveClasses}
            ${disabled ? 'select-none' : ''}
          `}
        >
          {label}
        </span>
      ) : (
        <label
          htmlFor={name}
          className={`
            text-sm font-medium
            text-black dark:text-white
            ${labelCursorClass}
          `}
        >
          {label}
        </label>
      )}

      <label
        htmlFor={name}
        aria-disabled={disabled}
        className={`
          group flex select-none items-center
          ${labelCursorClass}
        `}
      >
        <div className="relative">
          <input
            id={name}
            name={name}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="peer sr-only"
          />

          <div
            className="
              h-8 w-14 rounded-full
              border border-stroke
              bg-whiten shadow-inner
              transition-all duration-200

              peer-checked:border-primary
              peer-checked:bg-primary

              peer-focus-visible:border-primary
              peer-focus-visible:ring-2
              peer-focus-visible:ring-primary/10

              peer-disabled:cursor-not-allowed
              peer-disabled:bg-gray-2
              peer-disabled:shadow-none
              peer-disabled:peer-checked:bg-primary/60

              dark:border-form-strokedark
              dark:bg-form-strokedark
              dark:peer-checked:border-primary
              dark:peer-checked:bg-primary

              dark:peer-disabled:bg-meta-4
              dark:peer-disabled:peer-checked:bg-primary/60
            "
          />

          <div
            className="
              absolute left-1 top-1
              h-6 w-6 rounded-full
              border border-stroke
              bg-white shadow-1
              transition-all duration-200

              peer-checked:translate-x-6

              peer-disabled:cursor-not-allowed
              peer-disabled:shadow-none

              dark:border-strokedark
              dark:bg-bodydark1
              dark:peer-checked:border-white
              dark:peer-checked:bg-white
            "
          />
        </div>
      </label>
    </div>
  );
};

export default ToggleSwitch;